import {transparentize} from 'polished';
import React, {FC, useRef} from 'react';
import styled, {css} from 'styled-components';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {buildMarkdownDecoratedCountChildrenTextContentComponent} from '../../../../../srcCommon/formatters/markdown/markdownDecoratedComponents';
import {NodeTransform} from '../../../../../srcCommon/formatters/markdown/markdownFormatterTypes';
import {
  commonMakeMarkdownTransformMentions,
  MentionWrapperProps,
  OnMentionClick,
} from '../../../../../srcCommon/formatters/markdown/transforms/commonMarkdownTransformMentions';

/*
 * Props.
 */

interface MentionSpanStyleProps {
  $color?: string;
  $isUserMention?: boolean;
  $isClickable?: boolean;
}

/*
 * Style.
 */

const StyledMentionButton = styled(ChromelessButton)<MentionSpanStyleProps>`
  display: inline;
  color: ${(p) => p.$color || p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
  ${(p) => maybeAddBackground(p.$isUserMention)}
  ${(p) => maybeAddHover(p.$isClickable)}
`;

function maybeAddHover(isClickable?: boolean) {
  if (!isClickable) {
    return '';
  }

  return css`
    &:hover {
      background-color: ${(p) => p.theme.alphas.gray10};
    }
  `;
}

function maybeAddBackground(isUserMention?: boolean) {
  if (!isUserMention) {
    return '';
  }

  return css`
    background-color: ${(p) => transparentize(0.85, p.theme.palette.blue.shade40)};
    border-radius: 4px;
    padding: 1px 2px 2px;
    margin: 0 -1px;
  `;
}

/*
 * Components.
 */

const MentionWrapper: FC<MentionWrapperProps> = (props) => {
  const ref = useRef(null);
  const {alias, color, onMentionClick, children, isUserMention} = props;
  return (
    <StyledMentionButton
      $color={color}
      $isUserMention={isUserMention}
      $isClickable={isDefined(onMentionClick)}
      ref={ref}
      onClick={() => {
        onMentionClick?.(alias, ref);
      }}
    >
      {children}
    </StyledMentionButton>
  );
};

const DecoratedMentionWrapper = buildMarkdownDecoratedCountChildrenTextContentComponent(MentionWrapper);

/*
 * Transform.
 */

export function makeMarkdownTransformMentions(
  mentionableAliases: ReadonlySet<string>,
  color?: string | undefined,
  userAlias?: string | undefined,
  onMentionClick?: OnMentionClick,
): NodeTransform {
  return commonMakeMarkdownTransformMentions({
    mentionableAliases,
    userAlias,
    color,
    MentionWrapperComponent: DecoratedMentionWrapper,
    onMentionClick,
  });
}
